/**
 * Throttle (HTTP 429) helpers shared by every client error class (#1747 D1).
 *
 * Both `SuiteApiError` and the shell's `ApiClientError` carry `retryAfter`
 * (seconds, from the `Retry-After` header) and `receivedAt` (ms epoch, when the
 * response arrived). These helpers are duck-typed on `code === 429` so they
 * work for either class without importing it.
 */

export const THROTTLE_STATUS = 429;

/** No `Retry-After` header: back off 5s → 10s → 20s …, capped at 60s. */
export const THROTTLE_NO_HEADER_BASE_MS = 5_000;
export const THROTTLE_NO_HEADER_CAP_MS = 60_000;

/**
 * Floor for a stated wait, so a server that answers `Retry-After: 0` can't
 * make an unlimited retry loop spin.
 */
const MIN_STATED_WAIT_MS = 1_000;

/** User-facing message for a 429 — DRF's "Request was throttled…" is never shown. */
export const THROTTLED_MESSAGE = "Too many requests. Please wait a moment.";

/**
 * Parse a `Retry-After` header into whole seconds. Accepts delay-seconds
 * (`"30"`, `"2.5"`) or an HTTP-date. Returns `undefined` when absent or
 * unparseable.
 */
export function parseRetryAfter(value: string | null | undefined, now: number = Date.now()): number | undefined {
  if (value == null) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return Math.ceil(Number(trimmed));
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return undefined;
  return Math.max(0, Math.ceil((at - now) / 1000));
}

export interface ThrottleInfo {
  code: number;
  retryAfter?: number;
  receivedAt?: number;
}

export function isThrottled(err: unknown): err is ThrottleInfo {
  return typeof err === "object" && err !== null && (err as { code?: unknown }).code === THROTTLE_STATUS;
}

/**
 * Total wait (ms) that `err` asks for. `attempt` is the 0-based index of this
 * consecutive failure; it only matters when no `Retry-After` was sent.
 */
export function throttleWaitMs(err: ThrottleInfo, attempt = 0): number {
  const stated = err.retryAfter;
  if (typeof stated === "number" && Number.isFinite(stated) && stated >= 0) {
    return Math.max(stated * 1000, MIN_STATED_WAIT_MS);
  }
  return Math.min(THROTTLE_NO_HEADER_BASE_MS * 2 ** Math.max(0, attempt), THROTTLE_NO_HEADER_CAP_MS);
}

/** Epoch ms at which the wait for `err` is over. */
export function throttleDeadline(err: ThrottleInfo, attempt = 0): number {
  return (err.receivedAt ?? Date.now()) + throttleWaitMs(err, attempt);
}

/** Ms still to wait for `err` (never negative). */
export function throttleRemainingMs(err: ThrottleInfo, attempt = 0, now: number = Date.now()): number {
  return Math.max(0, throttleDeadline(err, attempt) - now);
}

/** Read `retryAfter` / `receivedAt` off a failed `Response`. */
export function throttleFieldsFrom(response: Response): { retryAfter?: number; receivedAt: number } {
  const receivedAt = Date.now();
  let header: string | null = null;
  try {
    header = response.headers?.get?.("Retry-After") ?? null;
  } catch {
    header = null;
  }
  return { retryAfter: parseRetryAfter(header, receivedAt), receivedAt };
}
